import {
  Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';

import { IArea } from 'src/app/_models/area.vm';
import { ISystemLevel } from 'src/app/_models/systemLevel.vm';
import { trackByAreaId, trackBySystemLevelId } from '../../utils/track-by';

@Component({
    selector: 'app-area-dropdown',
    templateUrl: './area-dropdown.component.html',
    styleUrls: ['./area-dropdown.component.scss'],
    standalone: false
})
export class AreaDropdownComponent implements OnInit, OnChanges, OnDestroy {
  public readonly trackByAreaId = trackByAreaId;
  public readonly trackBySystemLevelId = trackBySystemLevelId;


  /** Number of characters needed before a large level (practices) renders any rows. */
  public readonly minSearchChars: number = 3;
  /** Upper bound on rows rendered at once, so a level with thousands of areas stays responsive. */
  public readonly maxRenderedAreas: number = 100;
  /** Delay before a hovered level loads, so passing the cursor over the list doesn't fire requests. */
  private readonly hoverDelayMs: number = 250;

  @Input() public systemLevels: Array<ISystemLevel>;
  @Input() public activeLevel: ISystemLevel;
  /** Areas belonging to activeLevel. A separate input so a late-arriving array triggers ngOnChanges. */
  @Input() public areas: Array<IArea>;
  @Input() public areasLoading: boolean = false;
  @Input() public selectedArea: IArea;
  @Input() public levelsLoading: boolean = false;
  @Input() public searchControl: UntypedFormControl;
  @Input() public searchResults: Array<IArea>;
  @Input() public searchLoading: boolean = false;
  @Input() public searchError: boolean = false;
  @Input() public allowSearch: boolean = true;
  @Input() public disabled: boolean = false;
  @Input() public busy: boolean = false;
  /**
   * Renders the panel on the page rather than behind a trigger. Used by the mobile
   * sheet, where a dropdown inside a panel would be a menu within a menu.
   */
  @Input() public inline: boolean = false;

  @Output() public opened = new EventEmitter<void>();
  @Output() public levelActivated = new EventEmitter<ISystemLevel>();
  @Output() public areaSelected = new EventEmitter<{ area: IArea, fromSearch: boolean }>();

  @ViewChild(NgbDropdown) private dropdown: NgbDropdown;
  @ViewChild('searchInput') private searchInput: ElementRef<HTMLInputElement>;
  @ViewChildren('levelButton') private levelButtons: QueryList<ElementRef<HTMLButtonElement>>;
  @ViewChildren('areaButton') private areaButtons: QueryList<ElementRef<HTMLButtonElement>>;

  public isOpen: boolean = false;
  public searchMode: boolean = false;
  /** Narrow viewports drill down rather than showing both columns side by side. */
  public mobileStep: 'levels' | 'areas' = 'levels';
  public levelFilter: string = '';

  public visibleAreas: Array<IArea> = [];
  public matchCount: number = 0;
  public truncated: boolean = false;
  public awaitingSearch: boolean = false;

  private hoverTimer: any;
  private hoverMediaQuery: MediaQueryList;
  private desktopMediaQuery: MediaQueryList;

  constructor() {
    this.hoverMediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.desktopMediaQuery = window.matchMedia('(min-width: 768px)');
  }

  public ngOnInit(): void {
    // Inline has no trigger to open, so the panel is on screen from the moment it
    // is created and goes through the same set up as an opening dropdown. Left to
    // the next turn because the parent loads areas in response, which would change
    // inputs that have already been checked in this pass.
    if (this.inline) {
      setTimeout(() => this.onOpenChange(true));
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeLevel']) {
      this.levelFilter = '';
    }
    if (changes['activeLevel'] || changes['areas']) {
      this.computeVisibleAreas();
    }
  }

  public ngOnDestroy(): void {
    clearTimeout(this.hoverTimer);
  }

  // Open / close

  public onOpenChange(open: boolean): void {
    this.isOpen = open;
    if (open) {
      this.searchMode = false;
      this.mobileStep = 'levels';
      this.levelFilter = '';
      this.computeVisibleAreas();
      this.opened.emit();
    } else {
      clearTimeout(this.hoverTimer);
    }
  }

  public close(): void {
    if (this.dropdown) {
      this.dropdown.close();
    }
  }

  public get triggerLabel(): string {
    if (this.busy) {
      return 'Loading...';
    }
    return this.selectedArea ? this.selectedArea.AreaName : 'Select an area...';
  }

  // Search across all levels

  public toggleSearch(): void {
    this.searchMode = !this.searchMode;
    if (this.searchControl) {
      this.searchControl.setValue('');
    }
    if (this.searchMode) {
      setTimeout(() => this.searchInput?.nativeElement.focus());
    }
  }

  public get searchTerm(): string {
    return (this.searchControl?.value || '').toString();
  }

  public get searchTermTooShort(): boolean {
    return this.searchTerm.trim().length > 0 && this.searchTerm.trim().length < this.minSearchChars;
  }

  // System levels

  public isPracticeLevel(level: ISystemLevel): boolean {
    return level?.SystemLevelName == 'Practice';
  }

  public isActiveLevel(level: ISystemLevel): boolean {
    return level?.SystemLevelID == this.activeLevel?.SystemLevelID;
  }

  public activateLevel(level: ISystemLevel): void {
    clearTimeout(this.hoverTimer);
    this.levelActivated.emit(level);
  }

  /** Click always works, including on touch and on the practice level. */
  public onLevelClick(level: ISystemLevel): void {
    this.activateLevel(level);
    if (!this.desktopMediaQuery.matches) {
      this.mobileStep = 'areas';
    }
  }

  public onLevelEnter(level: ISystemLevel): void {
    // Practices are excluded: the list is large and always needs a search term anyway.
    if (!this.hoverMediaQuery.matches || !this.desktopMediaQuery.matches || this.isPracticeLevel(level)) {
      return;
    }
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => this.activateLevel(level), this.hoverDelayMs);
  }

  public onLevelLeave(): void {
    clearTimeout(this.hoverTimer);
  }

  public backToLevels(): void {
    this.mobileStep = 'levels';
  }

  // Areas within the active level

  public onLevelFilterChange(value: string): void {
    this.levelFilter = value;
    this.computeVisibleAreas();
  }

  public get showLevelFilter(): boolean {
    return !!this.activeLevel && (this.isPracticeLevel(this.activeLevel) || (this.areas || []).length > 15);
  }

  private computeVisibleAreas(): void {
    const all = this.areas || [];
    const term = this.levelFilter.trim().toLowerCase();

    // Practices only render once the user has narrowed the list down.
    this.awaitingSearch = this.isPracticeLevel(this.activeLevel) && term.length < this.minSearchChars;
    if (this.awaitingSearch) {
      this.visibleAreas = [];
      this.matchCount = 0;
      this.truncated = false;
      return;
    }

    const matches = term
      ? all.filter(a => (a.AreaName || '').toLowerCase().includes(term) || (a.AreaCode || '').toLowerCase().includes(term))
      : all;

    this.matchCount = matches.length;
    this.truncated = matches.length > this.maxRenderedAreas;
    this.visibleAreas = this.truncated ? matches.slice(0, this.maxRenderedAreas) : matches;
  }

  public selectArea(area: IArea, fromSearch: boolean): void {
    this.close();
    this.areaSelected.emit({ area: area, fromSearch: fromSearch });
  }

  public isSelectedArea(area: IArea): boolean {
    return area?.AreaID == this.selectedArea?.AreaID;
  }

  // Keyboard navigation across the two columns

  public onLevelKeydown(event: KeyboardEvent, index: number): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusIn(this.levelButtons, index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusIn(this.levelButtons, index - 1);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.activateLevel(this.systemLevels[index]);
        setTimeout(() => this.focusIn(this.areaButtons, 0));
        break;
    }
  }

  public onAreaKeydown(event: KeyboardEvent, index: number): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusIn(this.areaButtons, index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusIn(this.areaButtons, index - 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.focusActiveLevelButton();
        break;
    }
  }

  private focusIn(buttons: QueryList<ElementRef<HTMLButtonElement>>, index: number): void {
    const items = buttons?.toArray() || [];
    if (!items.length) {
      return;
    }
    const bounded = Math.max(0, Math.min(index, items.length - 1));
    items[bounded].nativeElement.focus();
  }

  private focusActiveLevelButton(): void {
    const index = (this.systemLevels || []).findIndex(l => this.isActiveLevel(l));
    this.focusIn(this.levelButtons, index < 0 ? 0 : index);
  }
}
